import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';

function AdminDashboard({ user, onBack }) {
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [checkedInKids, setCheckedInKids] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('orgs');
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', user.id)
      .single();

    console.log('Profile data:', data);
    console.log('Profile error:', error);

    if (!error && data) {
      setProfile(data);
      if (data.role === 'superadmin') {
        fetchAllOrganizations();
      } else {
        fetchOrgById(data.organization_id);
      }
    } else {
      // No profile found, try fetching all orgs anyway
      fetchAllOrganizations();
    }
    setLoading(false);
  };

  const fetchAllOrganizations = async () => {
    const { data, error } = await supabase
      .from('organizations')
      .select('*')
      .order('created_at', { ascending: false });

    if (!error) setOrganizations(data);
  };

  const fetchOrgById = async (orgId) => {
    const { data, error } = await supabase
      .from('organizations')
      .select('*')
      .eq('id', orgId)
      .single();

    if (!error) {
      setOrganizations([data]);
      handleSelectOrg(data);
    }
  };

  const handleSelectOrg = async (org) => {
    setSelectedOrg(org);
    setView('orgDetail');

    const { data: checkins, error: checkinError } = await supabase
      .from('checkins')
      .select('*')
      .eq('organization_id', org.id)
      .eq('status', 'checked_in')
      .order('checked_in_at', { ascending: false });

    if (checkinError || !checkins.length) {
      setCheckedInKids([]);
      return;
    }

    const childIds = checkins.map((c) => c.child_id);
    const { data: children } = await supabase
      .from('children')
      .select('*')
      .in('id', childIds);

    const results = checkins.map((checkin) => {
      const child = children.find((c) => c.id === checkin.child_id);
      return { ...child, checkin_id: checkin.id, checked_in_at: checkin.checked_in_at };
    });

    setCheckedInKids(results);
  };

  const handleCheckOut = async (childId, checkinId, childName) => {
    const { error } = await supabase
      .from('checkins')
      .update({ status: 'checked_out', checked_out_at: new Date() })
      .eq('id', checkinId);

    if (!error) {
      setCheckedInKids(checkedInKids.filter((k) => k.checkin_id !== checkinId));
    }
  };

  const getOrgIcon = (type) => {
    if (type === 'school') return '🏫';
    if (type === 'daycare') return '🧸';
    if (type === 'church') return '⛪';
    if (type === 'event') return '🎉';
    return '🏢';
  };

  if (loading) return <p style={{ textAlign: 'center', color: '#888' }}>Loading...</p>;

  return (
    <div>
      {view === 'orgs' && (
        <>
          <div className="top-bar">
            <button className="btn btn-gray" onClick={onBack}>← Back</button>
            <span className="badge" style={{ fontSize: '14px', padding: '6px 12px' }}>
              {profile?.role === 'superadmin' ? '⭐ Super Admin' : '🏫 Org Admin'}
            </span>
          </div>

          <div className="header">
            <h2>🛠️ Admin Panel</h2>
            <p>Select an organization to manage</p>
          </div>

          <div className="stat-box">
            <h2>{organizations.length}</h2>
            <p>Total Organizations</p>
          </div>

          <p className="section-title">Organizations</p>
          {organizations.map((org) => (
            <div key={org.id} className="org-card" onClick={() => handleSelectOrg(org)}>
              <div className="org-icon">{getOrgIcon(org.type)}</div>
              <div>
                <h3>{org.name}</h3>
                <p>{org.type}</p>
              </div>
            </div>
          ))}
        </>
      )}

      {view === 'orgDetail' && selectedOrg && (
        <>
          <div className="top-bar">
            {profile?.role === 'superadmin' && (
              <button className="btn btn-gray" onClick={() => setView('orgs')}>← All Orgs</button>
            )}
            {profile?.role !== 'superadmin' && (
              <button className="btn btn-gray" onClick={onBack}>← Back</button>
            )}
          </div>

          <div className="header">
            <h2>{getOrgIcon(selectedOrg.type)} {selectedOrg.name}</h2>
            <p style={{ textTransform: 'capitalize' }}>{selectedOrg.type}</p>
          </div>

          <div className="stat-box">
            <h2>{checkedInKids.length}</h2>
            <p>Children Currently Checked In</p>
          </div>

          <p className="section-title">Checked In Children</p>

          {checkedInKids.length === 0 && (
            <p style={{ textAlign: 'center', color: '#999' }}>No children currently checked in.</p>
          )}

          {checkedInKids.map((kid) => (
            <div key={kid.checkin_id} className="kid-card">
              <div className="kid-info">
                <h3>👶 {kid.full_name}</h3>
                <p>Parent: {kid.parent_name}</p>
                <p>Phone: {kid.parent_phone}</p>
                <p>In at: {new Date(kid.checked_in_at).toLocaleTimeString()}</p>
              </div>
              <button
                className="btn btn-red btn-small"
                onClick={() => handleCheckOut(kid.id, kid.checkin_id, kid.full_name)}
              >
                Check Out
              </button>
            </div>
          ))}

          {/* Staff Management — orgadmin and superadmin only */}
          {profile?.role !== 'staff' && (
            <>
              <hr className="divider" />
              <p className="section-title">Add Staff Member</p>
              <AddStaff orgId={selectedOrg.id} />
            </>
          )}
        </>
      )}
    </div>
  );
}

function AddStaff({ orgId }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState('staff');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const handleAddStaff = async () => {
    setMessage('');
    setError('');

    if (!email || !password) {
      setError('Please enter email and password.');
      return;
    }

    const { data, error: signUpError } = await supabase.auth.signUp({ email, password });

    if (signUpError) {
      setError('Error: ' + signUpError.message);
      return;
    }

    const { error: profileError } = await supabase
      .from('profiles')
      .insert([{ id: data.user.id, email, role, organization_id: orgId }]);

    if (profileError) {
      setError('Error saving profile: ' + profileError.message);
      return;
    }

    setMessage(`✅ ${role === 'orgadmin' ? 'Admin' : 'Staff member'} added successfully!`);
    setEmail('');
    setPassword('');
    setRole('staff');
  };

  return (
    <div>
      <input className="input" placeholder="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
      <input className="input" placeholder="Temporary Password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} />
      <select className="input" value={role} onChange={(e) => setRole(e.target.value)}>
        <option value="staff">Staff — Dashboard only</option>
        <option value="orgadmin">Admin — Full access</option>
      </select>
      <button className="btn btn-primary" onClick={handleAddStaff}>Add Member</button>
      {message && <div className="success-message">{message}</div>}
      {error && <div className="error-message">{error}</div>}
    </div>
  );
}

export default AdminDashboard;